// pages/Leaderboard.tsx
// Full leaderboard page — fetches from Redis via /api/leaderboard,
// polls every 15s, matches the 10KSquadBot dark/pink Tailwind aesthetic.

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { Trophy, RefreshCw, LogOut, ExternalLink, Wifi, WifiOff } from 'lucide-react'
import clsx from 'clsx'
import { usePlayer } from '../hooks/usePlayer'
import type { LeaderboardEntry, GameId } from '../hooks/usePlayer'

// ─── Config ───────────────────────────────────────────────────────────────────

const GAMES: Record<GameId, { label: string; short: string; color: string; glow: string }> = {
  'parrot-cart':       { label: 'Parrot Cart',       short: 'CART',   color: 'text-yellow-400',  glow: 'shadow-yellow-500/20'  },
  'parrot-jump':       { label: 'Parrot Jump',       short: 'JUMP',   color: 'text-cyan-400',    glow: 'shadow-cyan-500/20'    },
  'parrot-save':       { label: 'Parrot Save',       short: 'SAVE',   color: 'text-pink-400',    glow: 'shadow-pink-500/20'    },
  'parrots-vs-znakes': { label: 'Parrots vs Znakes', short: 'ZNAKES', color: 'text-green-400',   glow: 'shadow-green-500/20'   },
}

const RANK_STYLES = [
  { text: 'text-yellow-400', border: 'border-l-yellow-400', bg: 'bg-yellow-400/5',  label: '1ST' },
  { text: 'text-slate-300',  border: 'border-l-slate-400',  bg: 'bg-slate-400/5',   label: '2ND' },
  { text: 'text-amber-600',  border: 'border-l-amber-600',  bg: 'bg-amber-700/5',   label: '3RD' },
]

const POLL_INTERVAL = 15_000 // 15 seconds

// ─── Avatar ───────────────────────────────────────────────────────────────────

function Avatar({ username, size = 36 }: { username: string; size?: number }) {
  const [status, setStatus] = useState<'loading' | 'ok' | 'error'>('loading')
  const initials = username.slice(0, 2).toUpperCase()
  const px = `${size}px`

  return (
    <div className="relative flex-shrink-0" style={{ width: px, height: px }}>
      {/* Initials fallback — always mounted */}
      <div
        className={clsx(
          'absolute inset-0 rounded-full flex items-center justify-center',
          'bg-pink-950/60 border border-pink-900/40',
          'font-mono font-bold text-pink-400',
          'transition-opacity duration-200',
          status === 'ok' ? 'opacity-0' : 'opacity-100',
        )}
        style={{ fontSize: Math.floor(size * 0.33) }}
      >
        {initials}
      </div>
      {/* X profile picture */}
      <img
        src={`https://unavatar.io/twitter/${username}`}
        alt={`@${username}`}
        loading="lazy"
        onLoad={() => setStatus('ok')}
        onError={() => setStatus('error')}
        className={clsx(
          'absolute inset-0 w-full h-full rounded-full object-cover',
          'transition-opacity duration-300',
          status === 'ok' ? 'opacity-100' : 'opacity-0',
        )}
      />
    </div>
  )
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl border border-pink-900/20 animate-pulse">
      <div className="w-7 h-3 rounded bg-pink-900/30 flex-shrink-0" />
      <div className="w-9 h-9 rounded-full bg-pink-900/30 flex-shrink-0" />
      <div className="flex-1 space-y-2">
        <div className="h-3 w-28 rounded bg-pink-900/30" />
        <div className="h-2 w-16 rounded bg-pink-900/20" />
      </div>
      <div className="h-4 w-14 rounded bg-pink-900/30" />
    </div>
  )
}

// ─── Row ──────────────────────────────────────────────────────────────────────

function Row({
  entry,
  rank,
  game,
  currentUser,
}: {
  entry: LeaderboardEntry
  rank: number
  game: GameId
  currentUser: string | null
}) {
  const isTop3  = rank <= 3
  const isMe    = entry.username.toLowerCase() === currentUser?.toLowerCase()
  const style   = isTop3 ? RANK_STYLES[rank - 1] : null
  const gameInfo = GAMES[game]

  return (
    <div
      className={clsx(
        'flex items-center gap-3',
        'px-4 py-3 rounded-xl',
        'border border-l-2',
        'transition-colors duration-150',
        isMe
          ? 'bg-pink-600/8 border-pink-600/30 border-l-pink-500'
          : isTop3
          ? `${style!.bg} border-pink-900/20 ${style!.border}`
          : 'bg-white/[0.02] border-pink-900/15 border-l-transparent hover:bg-pink-900/10',
      )}
    >
      {/* Rank */}
      <span
        className={clsx(
          'font-mono text-[10px] tracking-widest flex-shrink-0 w-7 text-center',
          isMe ? 'text-pink-400' : isTop3 ? style!.text : 'text-pink-900',
        )}
      >
        {isTop3 ? style!.label : `#${rank}`}
      </span>

      {/* Avatar */}
      <Avatar username={entry.username} size={36} />

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`https://x.com/${entry.username}`}
            target="_blank"
            rel="noopener noreferrer"
            className={clsx(
              'font-mono text-sm font-bold tracking-wide truncate',
              'hover:underline underline-offset-2',
              isMe ? 'text-pink-300' : 'text-white',
            )}
          >
            @{entry.username}
          </a>
          {isMe && (
            <span className="flex-shrink-0 font-mono text-[9px] tracking-widest text-pink-400 border border-pink-600/40 bg-pink-600/10 rounded px-1.5 py-0.5">
              YOU
            </span>
          )}
        </div>
        {/* Show game label only on "all" tab */}
        {!game && (
          <p className={clsx('font-mono text-[10px] mt-0.5', gameInfo.color)}>
            {gameInfo.label}
          </p>
        )}
      </div>

      {/* Score */}
      <span
        className={clsx(
          'font-mono font-bold flex-shrink-0 tabular-nums',
          'text-sm sm:text-base',
          isMe ? 'text-pink-300' : isTop3 ? style!.text : 'text-white/80',
        )}
      >
        {entry.score.toLocaleString()}
      </span>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────

type AllData = Partial<Record<GameId, LeaderboardEntry[]>>

export default function Leaderboard() {
  const navigate             = useNavigate()
  const { username, clearUsername } = usePlayer()

  const [filter, setFilter]   = useState<GameId>('parrot-cart')
  const [data, setData]       = useState<AllData>({})
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState<string | null>(null)
  const [online, setOnline]   = useState(true)
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)
  const timerRef              = useRef<ReturnType<typeof setInterval> | null>(null)

  const fetchAll = useCallback(async (silent = false) => {
    if (!silent) setLoading(true)
    setError(null)

    try {
      const res = await fetch('/api/leaderboard?game=all')
      if (!res.ok) throw new Error(`Server error ${res.status}`)
      const json = await res.json()
      setData(json.data as AllData)
      setLastUpdated(new Date())
      setOnline(true)
    } catch (e) {
      setError('Could not reach the server.')
      setOnline(false)
      console.error('[Leaderboard fetch]', e)
    } finally {
      setLoading(false)
    }
  }, [])

  // Initial load + polling
  useEffect(() => {
    fetchAll()
    timerRef.current = setInterval(() => fetchAll(true), POLL_INTERVAL)
    return () => { if (timerRef.current) clearInterval(timerRef.current) }
  }, [fetchAll])

  const entries = data[filter] ?? []

  // My stats across all games
  const myEntries = Object.entries(data).flatMap(([game, list]) =>
    (list ?? [])
      .filter(e => e.username.toLowerCase() === username?.toLowerCase())
      .map(e => ({ ...e, game: game as GameId }))
  )
  const myBest = myEntries.reduce((m, e) => e.score > m ? e.score : m, 0)
  const myRankInFilter = entries.findIndex(
    e => e.username.toLowerCase() === username?.toLowerCase()
  )

  return (
    <div className="min-h-dvh bg-[#0a0008] flex flex-col items-center px-4 py-8 sm:py-12">
      {/* Scanlines */}
      <div
        className="pointer-events-none fixed inset-0 z-0"
        style={{
          backgroundImage:
            'repeating-linear-gradient(0deg,transparent,transparent 2px,rgba(0,0,0,0.07) 2px,rgba(0,0,0,0.07) 4px)',
        }}
      />

      <div className="relative z-10 w-full max-w-lg">

        {/* ── Header ──────────────────────────────────────────────────── */}
        <div className="flex items-start justify-between mb-6 gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Trophy size={16} className="text-yellow-400" />
              <h1 className="font-display text-xl text-white tracking-widest">LEADERBOARD</h1>
            </div>
            <p className="font-mono text-[10px] text-pink-400/50 tracking-widest">
              PARROT GAMES · 10K SQUAD
            </p>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Online indicator */}
            <span className={clsx('flex items-center gap-1 font-mono text-[9px] tracking-wide', online ? 'text-green-400/60' : 'text-red-400/60')}>
              {online ? <Wifi size={10} /> : <WifiOff size={10} />}
              {online ? 'LIVE' : 'OFFLINE'}
            </span>

            {/* Refresh */}
            <button
              onClick={() => fetchAll()}
              disabled={loading}
              className="p-1.5 rounded-lg border border-pink-900/30 text-pink-500/60 hover:text-pink-300 hover:border-pink-600/40 transition-all disabled:opacity-40"
              aria-label="Refresh"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            </button>

            {/* Back */}
            <button
              onClick={() => navigate(-1)}
              className="font-mono text-[9px] tracking-widest text-pink-500/50 hover:text-pink-300 border border-pink-900/30 hover:border-pink-600/40 rounded-lg px-3 py-1.5 transition-all"
            >
              ← BACK
            </button>
          </div>
        </div>

        {/* ── My Stats Bar ────────────────────────────────────────────── */}
        {username && (
          <div className="flex items-center gap-3 mb-5 p-3 rounded-xl border border-pink-900/30 bg-pink-950/20">
            <Avatar username={username} size={32} />
            <div className="flex-1 min-w-0">
              <p className="font-mono text-xs text-white font-bold truncate">@{username}</p>
              {myBest > 0 ? (
                <p className="font-mono text-[10px] text-pink-400/60">
                  Best: <span className="text-pink-300">{myBest.toLocaleString()}</span>
                  {myRankInFilter >= 0 && (
                    <> · Rank <span className="text-pink-300">#{myRankInFilter + 1}</span> here</>
                  )}
                </p>
              ) : (
                <p className="font-mono text-[10px] text-pink-900">No scores yet — go play!</p>
              )}
            </div>
            <button
              onClick={clearUsername}
              className="flex-shrink-0 p-1.5 rounded-lg text-pink-900 hover:text-pink-400 transition-colors"
              title="Change username"
            >
              <LogOut size={13} />
            </button>
          </div>
        )}

        {/* ── Game Filter Tabs ─────────────────────────────────────────── */}
        <div className="flex gap-2 mb-5 overflow-x-auto pb-1 scrollbar-none">
          {(Object.keys(GAMES) as GameId[]).map(id => {
            const g      = GAMES[id]
            const active = filter === id
            return (
              <button
                key={id}
                onClick={() => setFilter(id)}
                className={clsx(
                  'flex-shrink-0 font-mono text-[9px] tracking-[2px] uppercase',
                  'px-3 py-2 rounded-lg border transition-all duration-150',
                  active
                    ? `border-pink-600/50 bg-pink-600/20 ${g.color}`
                    : 'border-pink-900/30 text-pink-900 hover:border-pink-800/50 hover:text-pink-500',
                )}
              >
                {g.short}
              </button>
            )
          })}
        </div>

        {/* ── Game title ─────────────────────────────────────────────────── */}
        <div className="flex items-center justify-between mb-3">
          <p className={clsx('font-mono text-xs font-bold tracking-widest uppercase', GAMES[filter].color)}>
            {GAMES[filter].label}
          </p>
          {lastUpdated && (
            <p className="font-mono text-[9px] text-pink-900">
              Updated {lastUpdated.toLocaleTimeString()}
            </p>
          )}
        </div>

        {/* ── Error ─────────────────────────────────────────────────────── */}
        {error && (
          <div className="mb-4 px-4 py-3 rounded-xl border border-red-900/40 bg-red-950/20 font-mono text-xs text-red-400">
            {error} — Showing cached data if available.
          </div>
        )}

        {/* ── Rows ──────────────────────────────────────────────────────── */}
        <div className="flex flex-col gap-2">
          {loading && entries.length === 0 ? (
            Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)
          ) : entries.length === 0 ? (
            <div className="text-center py-16">
              <p className="font-mono text-xs text-pink-900 tracking-widest">
                NO SCORES YET · GO PLAY!
              </p>
            </div>
          ) : (
            entries.map((e, i) => (
              <Row
                key={`${e.username}_${i}`}
                entry={e}
                rank={i + 1}
                game={filter}
                currentUser={username}
              />
            ))
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <p className="mt-8 text-center font-mono text-[9px] text-pink-950 tracking-wider">
          Personal best per player · Powered by Upstash Redis · Auto-refreshes every 15s
        </p>
      </div>
    </div>
  )
}